import Papa from "papaparse";
import { detectListColumns } from "../../AudiencePage/audienceListRows";
import {
  CSV_MIME_TYPE,
  EMAIL_PATTERN,
  LIST_FIELDS,
  type ColumnMapping,
} from "./createList.constants";

export interface ParsedCsv {
  file: File;
  headers: string[];
  rows: string[][];
}

export interface ListCleanup {
  keptRows: string[][];
  skippedRows: string[][];
  duplicateCount: number;
  missingPhoneCount: number;
}

export const parseCsvFile = (file: File): Promise<ParsedCsv> =>
  new Promise((resolve, reject) => {
    Papa.parse<string[]>(file, {
      header: false,
      skipEmptyLines: "greedy",
      complete: ({ data }) => {
        const [headerRow = [], ...rows] = data;
        resolve({ file, headers: headerRow.map(header => header.trim()), rows });
      },
      error: reject,
    });
  });

export const suggestMapping = (headers: string[]): ColumnMapping => {
  const detected = detectListColumns(headers);
  return {
    email: detected.email ?? null,
    firstName: detected.firstName ?? detected.fullName ?? null,
    lastName: detected.lastName ?? null,
    phone: detected.phone ?? null,
  };
};

const readCell = (row: string[], headers: string[], header: string | null) =>
  header ? (row[headers.indexOf(header)] ?? "").trim() : "";

export const getFirstRowValue = (csv: ParsedCsv, header: string | null) =>
  csv.rows.length ? readCell(csv.rows[0], csv.headers, header) : "";

export const cleanupRows = (csv: ParsedCsv, mapping: ColumnMapping): ListCleanup => {
  const seenEmails = new Set<string>();
  const result: ListCleanup = {
    keptRows: [],
    skippedRows: [],
    duplicateCount: 0,
    missingPhoneCount: 0,
  };

  csv.rows.forEach(row => {
    const email = readCell(row, csv.headers, mapping.email).toLowerCase();
    if (!EMAIL_PATTERN.test(email)) {
      result.skippedRows.push(row);
      return;
    }
    if (seenEmails.has(email)) {
      result.duplicateCount += 1;
      return;
    }
    seenEmails.add(email);
    result.keptRows.push(row);
    if (!readCell(row, csv.headers, mapping.phone)) {
      result.missingPhoneCount += 1;
    }
  });

  return result;
};

const getMappedHeaders = (mapping: ColumnMapping) =>
  LIST_FIELDS.map(field => mapping[field.key]).filter((header): header is string => Boolean(header));

export const buildCsvFile = (
  headers: string[],
  rows: string[][],
  fileName: string,
  onlyHeaders?: string[],
): File => {
  const columns = onlyHeaders ?? headers;
  const indexes = columns.map(header => headers.indexOf(header));
  const csv = Papa.unparse({
    fields: columns,
    data: rows.map(row => indexes.map(index => row[index] ?? "")),
  });
  return new File([csv], fileName, { type: CSV_MIME_TYPE });
};

export const buildUploadFile = (csv: ParsedCsv, mapping: ColumnMapping, rows: string[][]) =>
  buildCsvFile(csv.headers, rows, csv.file.name, getMappedHeaders(mapping));

export const formatFileSize = (bytes: number, formatValue: (_value: number) => string) => {
  const kilobytes = bytes / 1024;
  return kilobytes < 1024
    ? `${formatValue(Math.max(1, Math.round(kilobytes)))} KB`
    : `${formatValue(Math.round((kilobytes / 1024) * 10) / 10)} MB`;
};
